/**
 * CareRecipientProfile Page
 *
 * Settings page for viewing and editing the active CaseFile (care recipient's information).
 * Contains 4 tabs: Basic Info, Care Team, Insurance, Authorization
 *
 * Based on SETTINGS_FUNCTIONAL_SPEC.md Section 1 "Care Recipient Profile Page"
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import { ChevronDown, Plus, Loader2 } from 'lucide-react';
import { SettingsLayout } from '../../components/settings/SettingsLayout';
import { BasicInfoTab } from '../../components/settings/BasicInfoTab';
import { CareTeamTab } from '../../components/settings/CareTeamTab';
import { InsuranceTab } from '../../components/settings/InsuranceTab';
import { AuthorizationTab } from '../../components/settings/AuthorizationTab';
import type { CaseFile, CaseFileSummary, ListCaseFilesResponse, GetCaseFileResponse } from '../../types/settings';

type TabId = 'basic-info' | 'care-team' | 'insurance' | 'authorization';

interface Tab {
  id: TabId;
  label: string;
}

const TABS: Tab[] = [
  { id: 'basic-info', label: 'Basic Info' },
  { id: 'care-team', label: 'Care Team' },
  { id: 'insurance', label: 'Insurance' },
  { id: 'authorization', label: 'Authorization' },
];

export function CareRecipientProfile(): JSX.Element {
  const navigate = useNavigate();

  // Active tab state
  const [activeTab, setActiveTab] = useState<TabId>('basic-info');

  // CaseFiles state
  const [caseFiles, setCaseFiles] = useState<CaseFileSummary[]>([]);
  const [activeCaseFileId, setActiveCaseFileId] = useState<string | null>(null);
  const [activeCaseFile, setActiveCaseFile] = useState<CaseFile | null>(null);

  // Loading states
  const [loadingCaseFiles, setLoadingCaseFiles] = useState(true);
  const [loadingCaseFile, setLoadingCaseFile] = useState(false);

  // Dropdown state
  const [showCaseFileDropdown, setShowCaseFileDropdown] = useState(false);

  // Error state
  const [error, setError] = useState<string | null>(null);

  // Fetch all CaseFiles on mount
  useEffect(() => {
    const fetchCaseFiles = async () => {
      try {
        const listUserCaseFiles = httpsCallable<Record<string, never>, ListCaseFilesResponse>(
          functions,
          'listUserCaseFiles'
        );
        const result = await listUserCaseFiles({});

        if (result.data.success) {
          setCaseFiles(result.data.caseFiles || []);
          // Set first CaseFile as active if we have any
          if (result.data.caseFiles && result.data.caseFiles.length > 0) {
            setActiveCaseFileId(result.data.caseFiles[0].caseFileId);
          }
        } else {
          setError(result.data.error || 'Failed to load CaseFiles');
        }
      } catch (err) {
        console.error('Error fetching CaseFiles:', err);
        setError('Failed to load CaseFiles');
      } finally {
        setLoadingCaseFiles(false);
      }
    };

    fetchCaseFiles();
  }, []);

  // Fetch active CaseFile details when selection changes
  useEffect(() => {
    if (!activeCaseFileId) return;

    const fetchCaseFile = async () => {
      setLoadingCaseFile(true);
      try {
        const getCaseFile = httpsCallable<{ caseFileId: string }, GetCaseFileResponse>(
          functions,
          'getCaseFile'
        );
        const result = await getCaseFile({ caseFileId: activeCaseFileId });

        if (result.data.success && result.data.caseFile) {
          setActiveCaseFile(result.data.caseFile);
        } else {
          setError(result.data.error || 'Failed to load CaseFile');
        }
      } catch (err) {
        console.error('Error fetching CaseFile:', err);
        setError('Failed to load CaseFile');
      } finally {
        setLoadingCaseFile(false);
      }
    };

    fetchCaseFile();
  }, [activeCaseFileId]);

  // Handle CaseFile selection
  const handleCaseFileSelect = (caseFileId: string) => {
    setActiveCaseFileId(caseFileId);
    setShowCaseFileDropdown(false);
  };

  // Handle refresh after updates
  const handleCaseFileUpdated = () => {
    if (activeCaseFileId) {
      // Re-fetch the active CaseFile
      const fetchCaseFile = async () => {
        try {
          const getCaseFile = httpsCallable<{ caseFileId: string }, GetCaseFileResponse>(
            functions,
            'getCaseFile'
          );
          const result = await getCaseFile({ caseFileId: activeCaseFileId });

          if (result.data.success && result.data.caseFile) {
            setActiveCaseFile(result.data.caseFile);
          }
        } catch (err) {
          console.error('Error refreshing CaseFile:', err);
        }
      };

      fetchCaseFile();
    }
  };

  // Navigate to create CaseFile page to add new care recipient
  const handleAddCareRecipient = () => {
    navigate('/create-casefile');
  };

  // Get current CaseFile name for dropdown
  const currentCaseFileName = caseFiles.find((cf) => cf.caseFileId === activeCaseFileId)?.name || 'Select Care Recipient';

  // Loading state
  if (loadingCaseFiles) {
    return (
      <SettingsLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-evergreen" />
        </div>
      </SettingsLayout>
    );
  }

  // No CaseFiles state
  if (caseFiles.length === 0) {
    return (
      <SettingsLayout>
        <div className="text-center py-20">
          <h2 className="text-2xl font-bold text-ink mb-4">No Care Recipients</h2>
          <p className="text-muted mb-6">
            You haven't added a care recipient yet. Get started by adding someone you care for.
          </p>
          <button onClick={handleAddCareRecipient} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Add Care Recipient
          </button>
        </div>
      </SettingsLayout>
    );
  }

  return (
    <SettingsLayout>
      {/* Page Header */}
      <div className="mb-6">
        <h2 className="text-[28px] font-bold text-ink">Care Recipient Profile</h2>
        <p className="text-muted text-[15px]">The person you're caring for</p>
      </div>

      {/* CaseFile Selector (only show if multiple CaseFiles) */}
      {caseFiles.length > 1 && (
        <div className="relative mb-6">
          <button
            onClick={() => setShowCaseFileDropdown(!showCaseFileDropdown)}
            className="flex items-center gap-3 px-4 py-3 bg-white border border-soft-neutral rounded-lg hover:border-evergreen transition-colors w-full max-w-md"
          >
            <div className="flex-1 text-left">
              <div className="text-xs text-muted">Managing</div>
              <div className="font-semibold text-ink flex items-center gap-2">
                {currentCaseFileName}
                <ChevronDown className="w-4 h-4 text-muted" />
              </div>
            </div>
          </button>

          {/* Dropdown */}
          {showCaseFileDropdown && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowCaseFileDropdown(false)}
              />
              <div className="absolute top-full left-0 mt-1 w-full max-w-md bg-white border border-soft-neutral rounded-lg shadow-lg z-20">
                {caseFiles.map((cf) => (
                  <button
                    key={cf.caseFileId}
                    onClick={() => handleCaseFileSelect(cf.caseFileId)}
                    className={`w-full px-4 py-3 text-left hover:bg-airy transition-colors first:rounded-t-lg last:rounded-b-lg ${
                      cf.caseFileId === activeCaseFileId ? 'bg-airy font-semibold' : ''
                    }`}
                  >
                    {cf.name}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-soft-neutral mb-8">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-5 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              activeTab === tab.id
                ? 'text-evergreen border-evergreen'
                : 'text-muted border-transparent hover:text-ink'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Error Message */}
      {error && (
        <div className="alert alert-error mb-6" role="alert">
          {error}
        </div>
      )}

      {/* Loading CaseFile */}
      {loadingCaseFile && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-evergreen" />
        </div>
      )}

      {/* Tab Content */}
      {!loadingCaseFile && activeCaseFile && (
        <>
          {activeTab === 'basic-info' && (
            <BasicInfoTab
              caseFile={activeCaseFile}
              onUpdated={handleCaseFileUpdated}
            />
          )}

          {activeTab === 'care-team' && (
            <CareTeamTab
              caseFile={activeCaseFile}
              onUpdated={handleCaseFileUpdated}
            />
          )}

          {activeTab === 'insurance' && (
            <InsuranceTab
              caseFile={activeCaseFile}
              onUpdated={handleCaseFileUpdated}
            />
          )}

          {activeTab === 'authorization' && (
            <AuthorizationTab caseFile={activeCaseFile} />
          )}

          {/* Add Another Care Recipient Link */}
          <div className="mt-6 pt-4 border-t border-soft-neutral">
            <button
              onClick={handleAddCareRecipient}
              className="flex items-center gap-2 text-muted text-sm hover:text-evergreen transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add another care recipient
            </button>
          </div>
        </>
      )}
    </SettingsLayout>
  );
}

export default CareRecipientProfile;
